import React, { useRef, useId } from 'react'
import { Box, TextField, InputAdornment, IconButton } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'

import { faMagnifyingGlass } from '@fortawesome/pro-duotone-svg-icons/faMagnifyingGlass'
import { faTimes } from '@fortawesome/pro-duotone-svg-icons/faTimes'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const BUTTON_SIZE = {
    width: 38,
    height: 40,
} as const

const buttonWrapperSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...BUTTON_SIZE,
}

const iconButtonSx = {
    color: 'text.secondary',
    bgcolor: 'action.hover',
    '&:hover': {
        color: 'text.primary',
        bgcolor: 'action.hover',
    },
}

export interface DefaultSearchbarProps {
    kedo: any
    handleChange?: (e: any) => void
    /** Called with the current value for both Enter and Search-button submission. */
    onSearch?: (value: string) => void
    searchName?: string | null
    onReset?: () => void
    /** Styles for the built-in clear IconButton. */
    resetButtonSx?: SxProps<Theme>
    appendButton?: React.ReactNode
    /** @deprecated Prefer onSearch — kept for existing admin screens. */
    handleSearch?: (input: any) => void
    /** @deprecated Prefer onReset — kept for existing admin screens. */
    resetButton?: () => React.ReactNode
}

function DefaultSearchbar({
    kedo,
    handleChange,
    onSearch,
    searchName,
    onReset,
    resetButtonSx,
    appendButton,
    handleSearch,
    resetButton,
}: DefaultSearchbarProps) {
    const inputRef = useRef<HTMLInputElement>(null)
    const inputId = useId()
    const searchLabel = kedo.t('Search')
    const clearLabel = kedo.t('Clear search')
    const showReset = Boolean(searchName && searchName.length > 0)

    const submitSearch = () => {
        const value = inputRef.current?.value ?? searchName ?? ''

        if (onSearch) {
            onSearch(value)
            return
        }

        handleSearch?.(inputRef)
    }

    const handleSearchKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>
    ) => {
        if (event.key !== 'Enter') {
            return
        }

        event.preventDefault()

        if (onSearch) {
            submitSearch()
            return
        }

        handleSearch?.(event)
    }

    const renderResetButton = () => {
        if (onReset) {
            return (
                <IconButton
                    type="button"
                    size="small"
                    aria-label={clearLabel}
                    onClick={() => {
                        onReset()
                        inputRef.current?.focus()
                    }}
                    sx={[
                        iconButtonSx,
                        ...(Array.isArray(resetButtonSx)
                            ? resetButtonSx
                            : resetButtonSx
                            ? [resetButtonSx]
                            : []),
                    ]}
                >
                    <FontAwesomeIcon icon={faTimes} aria-hidden="true" />
                </IconButton>
            )
        }

        if (showReset && resetButton) {
            return resetButton()
        }

        return null
    }

    return (
        <Box>
            <TextField
                inputRef={inputRef}
                name="searchName"
                autoComplete="off"
                onChange={handleChange}
                onKeyDown={handleSearchKeyDown}
                placeholder={`${searchLabel}...`}
                type="text"
                value={searchName ?? ''}
                fullWidth
                size="small"
                slotProps={{
                    htmlInput: {
                        id: inputId,
                        'data-cy': 'search-input',
                        'aria-label': searchLabel,
                        sx: {
                            pr: '0.875rem',
                            '&::placeholder': {
                                color: 'custom.hintText',
                                opacity: 1,
                            },
                        },
                    },
                    input: {
                        sx: { pr: 0 },
                        endAdornment: (
                            <InputAdornment
                                position="end"
                                sx={{ gap: 0, ml: 0, maxHeight: 'unset' }}
                            >
                                {showReset ? (
                                    <Box sx={buttonWrapperSx}>
                                        {renderResetButton()}
                                    </Box>
                                ) : null}
                                <IconButton
                                    type="button"
                                    aria-label={searchLabel}
                                    size="small"
                                    onClick={submitSearch}
                                    sx={{
                                        ...iconButtonSx,
                                        ...BUTTON_SIZE,
                                        borderRadius: 1,
                                        ml: 0,
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faMagnifyingGlass}
                                        aria-hidden="true"
                                    />
                                </IconButton>
                                {appendButton ?? null}
                            </InputAdornment>
                        ),
                    },
                }}
            />
        </Box>
    )
}

export default DefaultSearchbar
